import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiHelper } from "../../../utils/apiHelper";
import { toast } from "react-toastify";

export const fetchFundingDetails = createAsyncThunk(
    "funding/fetchFundingDetails",
    async (startupId, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.get(`/startup/${startupId}/funding-details`);
            if (response.data.code === "00000") {
                return response.data.result;
            }
            throw new Error("Failed to fetch funding details");
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const updateFundingDetails = createAsyncThunk(
    "funding/updateFundingDetails",
    async (fundingData, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.put("/startup/funding-details", fundingData);
            if (response.data.code === "00000") {
                toast.success('Fonlama detayları başarıyla güncellendi');
                return fundingData;
            }
            throw new Error("Failed to update funding details");
        } catch (error) {
            toast.error('Fonlama detayları güncellenirken bir hata oluştu');
            return rejectWithValue(error.message);
        }
    }
);

export const fetchFundUsageDetails = createAsyncThunk(
    "funding/fetchFundUsageDetails",
    async (startupId, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.get(`/startup/${startupId}/fund-usage-details`);
            return response.data.startupFundUsageDetails;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const updateFundUsageDetails = createAsyncThunk(
    "funding/updateFundUsageDetails",
    async (fundUsageData, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.post("/startup/fund-usage-details", fundUsageData);
            if (response.data.code === "00000") {
                toast.success('Fon kullanım detayları başarıyla güncellendi');
                return fundUsageData;
            }
            throw new Error("Failed to update fund usage details");
        } catch (error) {
            toast.error('Fon kullanım detayları güncellenirken bir hata oluştu');
            return rejectWithValue(error.message);
        }
    }
);

export const fetchGeneralJustification = createAsyncThunk(
    "funding/fetchGeneralJustification",
    async (startupId, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.get(`/startup/${startupId}/general-justification`);
            return response.data.startupGeneralJustification.description;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const updateGeneralJustification = createAsyncThunk(
    "funding/updateGeneralJustification",
    async ({ startupId, description }, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.put("/startup/general-justification", {
                startupId,
                description
            });
            if (response.data.code === "00000") {
                toast.success('Genel açıklama başarıyla güncellendi');
                return description;
            }
            throw new Error("Failed to update general justification");
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const updateFundUsageOrder = createAsyncThunk(
    'funding/updateFundUsageOrder',
    async ({ startupId, fundUsages }, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.put(`/startup/fund-usage-order`, {
                startupId,
                fundUsages
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

const fundingSlice = createSlice({
    name: "funding",
    initialState: {
        details: null,
        fundUsageDetails: [],
        generalJustification: '',
        loading: false,
        error: null
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Funding Details
            .addCase(fetchFundingDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchFundingDetails.fulfilled, (state, action) => {
                state.loading = false;
                state.details = action.payload;
                state.error = null;
            })
            .addCase(fetchFundingDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Update Funding Details
            .addCase(updateFundingDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateFundingDetails.fulfilled, (state, action) => {
                state.loading = false;
                state.details = { ...state.details, ...action.payload };
                state.error = null;
            })
            .addCase(updateFundingDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Fund Usage Details
            .addCase(fetchFundUsageDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchFundUsageDetails.fulfilled, (state, action) => {
                state.loading = false;
                state.fundUsageDetails = action.payload || [];
                state.error = null;
            })
            .addCase(fetchFundUsageDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(updateFundUsageDetails.fulfilled, (state, action) => {
                state.loading = false;
                // Yeni eklenen veriyi state'e ekle
                if (!state.fundUsageDetails) {
                    state.fundUsageDetails = [];
                }
                state.fundUsageDetails.push(action.payload);
            })
            .addCase(fetchGeneralJustification.fulfilled, (state, action) => {
                state.generalJustification = action.payload;
            })
            .addCase(updateGeneralJustification.fulfilled, (state, action) => {
                state.generalJustification = action.payload;
            })
            .addCase(updateFundUsageOrder.fulfilled, (state, action) => {
                state.fundUsageDetails = action.payload;
            })
            .addCase(updateFundUsageOrder.rejected, (state, action) => {
                state.error = action.payload;
            });
    }
});

export default fundingSlice.reducer;